import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CodeIcon from "@mui/icons-material/Code";
import MemoryIcon from "@mui/icons-material/Memory";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsightsIcon from "@mui/icons-material/Insights";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { assets } from "@/lib/assets";
import { products, type ProductId } from "@/lib/products";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { CtaSection } from "@/components/CtaSection";
import { PlatformArchitecture } from "@/components/PlatformArchitecture";
import { HeroBackground } from "@/components/HeroBackground";
import { btnPrimary, btnGhostOnDark, cardBase, container, eyebrow } from "@/lib/ui";

const slideImages = [assets.screenFullStack, assets.screenPlatform, assets.screenHardware];
const capIcons = [CodeIcon, MemoryIcon, PsychologyIcon, DesignServicesIcon];

type ProductInfo = { name: string; desc: string; tags: string[] };

function Hero() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  const slideText = t("home:hero.slides", { returnObjects: true }) as unknown as {
    title: string[];
    subtitle: string;
  }[];
  const slides = slideText.map((s, i) => ({ ...s, image: slideImages[i]! }));
  const proof = t("home:hero.proof", { returnObjects: true }) as unknown as {
    value: string;
    label: string;
  }[];

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [reduced, index, slides.length]);

  const go = (i: number) => setIndex((i + slides.length) % slides.length);
  const slide = slides[index]!;

  const onMouseMove = (e: React.MouseEvent) => {
    if (reduced) return;
    const { innerWidth, innerHeight } = window;
    setParallax({
      x: (e.clientX / innerWidth - 0.5) * 18,
      y: (e.clientY / innerHeight - 0.5) * 18,
    });
  };

  return (
    <section
      onMouseMove={onMouseMove}
      className="relative flex min-h-[84vh] min-h-[84svh] items-stretch overflow-hidden bg-[#070b10] text-white lg:min-h-screen lg:min-h-svh"
    >
      {/* Cinematic scene — per-slide crossfade + parallax */}
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="absolute inset-0"
          style={{ x: parallax.x, y: parallax.y }}
        >
          <img src={slide.image} alt="" className="h-full w-full object-cover" />
        </motion.div>
      </AnimatePresence>

      {/* Living energy network + flowing light, blended into the scene */}
      <HeroBackground className="absolute inset-0 h-full w-full [mix-blend-mode:screen]" />

      {/* Legibility + mood gradients */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#070b10] via-[#070b10]/82 to-[#070b10]/15" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070b10] via-[#070b10]/12 to-[#070b10]/45" />

      {/* Brand glow bloom */}
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[40rem] w-[40rem] rounded-full bg-[radial-gradient(circle,rgba(1,120,254,0.18),transparent_60%)] blur-3xl" />

      <div className={`${container} relative z-10 flex w-full flex-col`}>
        <div className="flex flex-1 flex-col justify-center pt-28 pb-12 lg:pt-32">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-white/80 backdrop-blur sm:text-sm">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-cyan opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-cyan" />
              </span>
              {t("home:hero.badge")}
            </span>
          </Reveal>

          <div className="mt-7 max-w-3xl">
            {/* All slides share one grid cell → block stays as tall as the
                tallest slide, so the CTAs / dots below never jump. */}
            <div className="grid">
              {slides.map((s, i) => {
                const active = i === index;
                return (
                  <motion.div
                    key={i}
                    className="[grid-area:1/1]"
                    initial="hide"
                    animate={active ? "show" : "hide"}
                    aria-hidden={!active}
                    style={{ pointerEvents: active ? undefined : "none" }}
                  >
                    <motion.h1
                      className="font-display text-[2.7rem] font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5rem]"
                      variants={{ show: { transition: { staggerChildren: 0.09 } } }}
                    >
                      {s.title.map((line, li) => (
                        <motion.span
                          key={li}
                          className="block"
                          variants={{
                            hide: { opacity: 0, y: 26 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
                          }}
                        >
                          {li === s.title.length - 1 ? (
                            <span
                              className="text-brand-gradient"
                              style={{ filter: "drop-shadow(0 8px 44px rgba(1,120,254,0.35))" }}
                            >
                              {line}
                            </span>
                          ) : (
                            line
                          )}
                        </motion.span>
                      ))}
                    </motion.h1>
                    <motion.p
                      className="mt-6 max-w-xl text-lg leading-relaxed text-[#c4ccd3] sm:text-xl"
                      variants={{
                        hide: { opacity: 0, y: 16 },
                        show: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.6, ease: "easeOut", delay: 0.3 },
                        },
                      }}
                    >
                      {s.subtitle}
                    </motion.p>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("capabilities")?.scrollIntoView({ behavior: "smooth" })
                }
                className={btnPrimary}
              >
                {t("home:hero.primaryCta")}
                <ArrowForwardIcon style={{ fontSize: 18 }} />
              </button>
              <Link to="/contact" className={btnGhostOnDark}>
                {t("home:hero.secondaryCta")}
              </Link>
            </div>

            <div className="mt-9 flex items-center gap-2.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`Slide ${i + 1}`}
                  className="h-1.5 overflow-hidden rounded-full bg-white/20 transition-all"
                  style={{ width: i === index ? 40 : 10 }}
                >
                  {i === index && (
                    <motion.span
                      key={`p-${index}`}
                      className="block h-full rounded-full bg-gradient-to-r from-brand to-brand-cyan"
                      initial={{ width: reduced ? "100%" : "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: reduced ? 0 : 5.5, ease: "linear" }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Proof strip — anchors the composition and signals scale */}
        <Reveal delay={0.2} className="hidden border-t border-white/10 py-6 sm:block">
          <dl className="grid max-w-3xl grid-cols-3 gap-6 lg:max-w-4xl">
            {proof.map((p) => (
              <div key={p.value} className="flex flex-col gap-1">
                <dt className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">
                  {p.value}
                </dt>
                <dd className="text-xs text-white/55 sm:text-sm">{p.label}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}

export function Home() {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  const capItems = t("home:capabilities.items", { returnObjects: true }) as unknown as {
    title: string;
    desc: string;
  }[];
  const focusPoints = t("home:focus.points", { returnObjects: true }) as unknown as string[];
  const items = t("products:items", { returnObjects: true }) as unknown as Record<ProductId, ProductInfo>;

  const stats = [
    { value: 7, label: t("home:stats.products") },
    { value: 15, suffix: "+", label: t("home:stats.algorithms") },
    { value: 5, label: t("home:stats.partners") },
    { value: 3, label: t("home:stats.directions") },
  ];

  const featuredIds: ProductId[] = ["pvPlatform", "pvController", "switchgear"];
  const featured = featuredIds.map((id) => products.find((p) => p.id === id)!);

  return (
    <>
      <Hero />

      <section id="capabilities" className="scroll-mt-24 bg-surface py-24 md:py-32">
        <div className={container}>
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className={eyebrow}>{t("home:capabilities.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
              {t("home:capabilities.heading")}
            </h2>
            <p className="mt-4 text-fg-muted">{t("home:capabilities.intro")}</p>
          </Reveal>

          <div className="mt-16 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {capItems.map((cap, i) => {
              const Icon = capIcons[i]!;
              return (
                <Reveal key={cap.title} delay={i * 0.08}>
                  <div className={`group relative h-full overflow-hidden ${cardBase} p-5 hover:-translate-y-1.5 hover:border-brand/50 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.5)] sm:p-7`}>
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(1,120,254,0.14),transparent_70%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-3 top-1 select-none font-display text-5xl font-bold leading-none text-fg/[0.04] sm:text-6xl"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20 transition-transform duration-300 group-hover:scale-110 sm:h-14 sm:w-14">
                      <Icon style={{ fontSize: 26 }} />
                    </div>
                    <h3 className="relative mt-5 text-base font-semibold sm:text-lg">{cap.title}</h3>
                    <p className="relative mt-2 text-sm leading-relaxed text-fg-muted">{cap.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-20">
            <Reveal className="mx-auto mb-8 max-w-3xl text-center">
              <h3 className="font-display text-2xl font-bold md:text-3xl">
                {t("home:arch.titleBefore")}
                <span className="text-brand">{t("home:arch.titleHighlight")}</span>
                {t("home:arch.titleAfter")}
              </h3>
              <p className="mt-3 text-fg-muted">{t("home:arch.subtitle")}</p>
            </Reveal>
            <PlatformArchitecture variant="compact" />
          </div>
        </div>
      </section>

      <section className="bg-bg py-24 md:py-32">
        <div className={`${container} grid items-center gap-12 lg:grid-cols-2 lg:gap-20`}>
          <Reveal>
            <p className={eyebrow}>{t("home:focus.eyebrow")}</p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight md:text-4xl">
              {t("home:focus.heading")}
            </h2>
            <p className="mt-5 text-fg-muted">{t("home:focus.intro")}</p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {focusPoints.map((point, i) => (
                <Reveal
                  as="li"
                  key={point}
                  delay={i * 0.08}
                  className="flex items-center gap-3 rounded-xl border border-hairline bg-elevated/40 px-4 py-3"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20">
                    <CheckCircleIcon style={{ fontSize: 17 }} />
                  </span>
                  <span className="text-sm font-medium">{point}</span>
                </Reveal>
              ))}
            </ul>
            <p className="mt-8 text-sm text-fg-faint">{t("home:focus.note")}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-5 rounded-[2rem] bg-[radial-gradient(circle_at_50%_35%,rgba(1,120,254,0.25),transparent_70%)] blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-hairline shadow-2xl">
                <img src={assets.energyDashboard} alt={t("home:focus.imageAlt")} className="w-full" />
                <div className="absolute inset-0 ring-1 ring-inset ring-brand/20" />
              </div>
              <div className="absolute -bottom-6 -left-4 hidden items-center gap-3 rounded-2xl border border-hairline bg-elevated/90 p-4 shadow-xl backdrop-blur lg:flex">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand-cyan text-white shadow-[0_8px_24px_-6px_rgba(1,120,254,0.6)]">
                  <InsightsIcon style={{ fontSize: 22 }} />
                </span>
                <div>
                  <p className="font-display text-xl font-bold leading-none">
                    {t("home:focus.badge.value")}
                  </p>
                  <p className="mt-1.5 text-xs text-fg-muted">{t("home:focus.badge.label")}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-bg py-12 md:py-16">
        <div className={container}>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#070b10] px-6 py-14 text-white shadow-[0_40px_120px_-50px_rgba(1,120,254,0.45)] sm:rounded-[2.5rem] md:px-12 md:py-16">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(1,120,254,0.22),transparent_55%)]" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/70 to-transparent" />
            <div className="relative grid grid-cols-2 gap-x-6 gap-y-12 sm:gap-x-8 lg:grid-cols-4 lg:divide-x lg:divide-white/10">
              {stats.map((stat, i) => (
                <Reveal key={stat.label} delay={i * 0.08} className="text-center lg:px-6">
                  <div
                    className="font-display text-6xl font-bold tracking-tight text-brand-gradient md:text-7xl"
                    style={{ filter: "drop-shadow(0 8px 30px rgba(1,120,254,0.3))" }}
                  >
                    <CountUp value={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="mt-4 text-sm tracking-wide text-[#9aa4ad]">{stat.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-24 md:py-32">
        <div className={container}>
          <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className={eyebrow}>{t("home:featured.eyebrow")}</p>
              <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
                {t("home:featured.heading")}
              </h2>
            </div>
            <Link
              to="/products"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-brand"
            >
              {t("actions.viewAllProjects")}
              <ArrowForwardIcon
                style={{ fontSize: 18 }}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {featured.map((proj, i) => {
              const info = items[proj.id];
              return (
                <Reveal key={proj.id} delay={i * 0.1}>
                  <div
                    className={`group relative flex h-full flex-col overflow-hidden ${cardBase} hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.45)]`}
                  >
                    <span className="pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-brand to-brand-cyan transition-transform duration-300 group-hover:scale-x-100" />
                    <button
                      type="button"
                      onClick={() => setLightbox({ src: proj.image, alt: info.name })}
                      aria-label={t("lightbox.zoom", { name: info.name })}
                      className={`relative block aspect-[16/10] w-full cursor-zoom-in overflow-hidden ${
                        proj.fit === "contain" ? "bg-bg p-3" : ""
                      }`}
                    >
                      <img
                        src={proj.image}
                        alt={info.name}
                        className={`h-full w-full transition-transform duration-500 group-hover:scale-105 ${
                          proj.fit === "contain" ? "object-contain" : "object-cover"
                        }`}
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/30 group-hover:opacity-100">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-[#0b0f14] shadow-lg">
                          <ZoomInIcon style={{ fontSize: 24 }} />
                        </span>
                      </span>
                    </button>
                    <div className="p-6">
                      <span className="text-xs font-medium uppercase tracking-wider text-brand">
                        {info.tags.slice(0, 2).join(" / ")}
                      </span>
                      <h3 className="mt-2 text-lg font-semibold">{info.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-fg-muted line-clamp-3">
                        {info.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaSection />

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm md:p-10"
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label={t("lightbox.close")}
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <CloseIcon />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-5xl"
            >
              <img
                src={lightbox.src}
                alt={lightbox.alt}
                className="max-h-[80vh] w-auto rounded-xl object-contain shadow-2xl"
              />
              <figcaption className="mt-3 text-center text-sm text-white/80">
                {lightbox.alt}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
